import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';

export interface SaveReportDialogProps {
  open: boolean;
  onClose: () => void;
  onSaveNew: (name: string, setAsDefault: boolean) => void;
  onOverwrite: (setAsDefault: boolean) => void;
  activeReportName: string | null;
}

export const SaveReportDialog: React.FC<SaveReportDialogProps> = ({
  open,
  onClose,
  onSaveNew,
  onOverwrite,
  activeReportName,
}) => {
  const theme = useTheme();
  const [name, setName] = useState('');
  const [mode, setMode] = useState<'choose' | 'new'>(activeReportName ? 'choose' : 'new');
  const [setAsDefault, setSetAsDefault] = useState(false);

  const handleOpen = () => {
    setName('');
    setMode(activeReportName ? 'choose' : 'new');
    setSetAsDefault(false);
  };

  const handleSaveNew = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSaveNew(trimmed, setAsDefault);
    onClose();
  };

  const handleOverwrite = () => {
    onOverwrite(setAsDefault);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionProps={{ onEnter: handleOpen }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>Save Report</DialogTitle>
      <DialogContent>
        {mode === 'choose' && activeReportName ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              You are currently viewing <strong>{activeReportName}</strong>.
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleOverwrite}
              sx={{
                textTransform: 'none',
                justifyContent: 'flex-start',
                py: 1.5,
                borderColor: theme.palette.v4.border,
                color: theme.palette.text.primary,
                '&:hover': {
                  borderColor: theme.palette.v4.accent,
                  bgcolor: `${theme.palette.v4.accent}08`,
                },
              }}
            >
              Overwrite &ldquo;{activeReportName}&rdquo;
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setMode('new')}
              sx={{
                textTransform: 'none',
                justifyContent: 'flex-start',
                py: 1.5,
                borderColor: theme.palette.v4.border,
                color: theme.palette.text.primary,
                '&:hover': {
                  borderColor: theme.palette.v4.accent,
                  bgcolor: `${theme.palette.v4.accent}08`,
                },
              }}
            >
              Save as new report
            </Button>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={setAsDefault}
                  onChange={(e) => setSetAsDefault(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Set as my default report
                </Typography>
              }
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Enter a name for your new report.
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Report name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveNew();
              }}
              slotProps={{ htmlInput: { 'aria-label': 'Report name' } }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={setAsDefault}
                  onChange={(e) => setSetAsDefault(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Set as my default report
                </Typography>
              }
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
        >
          Cancel
        </Button>
        {mode === 'new' && (
          <Button
            variant="contained"
            onClick={handleSaveNew}
            disabled={!name.trim()}
            sx={{
              textTransform: 'none',
              bgcolor: theme.palette.v4.accent,
              '&:hover': { bgcolor: theme.palette.v4.accent, opacity: 0.9 },
            }}
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
